import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../../../i18/config";
import { getApproval, getApprovals } from "../../../api";
import { useRedux } from "../../../hooks";
import { getEventUserBadges } from "../../../redux/Event/Badge/actions";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import Loader from "../../../components/Loader";
import { Approval } from "../types";
import { formatWorkflowDate } from "../format";
import { ApprovalStatusBadge } from "./ApprovalStatusBadge";
import ApprovalModal from "./ApprovalModal";
import { useDirectory } from "./useDirectory";
import ListCard from "../ui/ListCard";
import WorkflowTable from "../ui/WorkflowTable";
import RowActions, { actionsColumn } from "../ui/RowActions";

type Scope = 'mine' | 'requested' | 'all';

/**
 * Automation → Approvals: what is waiting for my decision, what I asked for,
 * and (admins, workflow managers) everything. ?approval=uuid opens one.
 */
const ApprovalList = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const location = useLocation();
    const { userLabel } = useDirectory();
    const [scope, setScope] = useState<Scope>('mine');
    const [status, setStatus] = useState('');
    const [items, setItems] = useState<Approval[] | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const [open, setOpen] = useState<Approval | null>(null);
    const { dispatch } = useRedux();

    const load = useCallback(() => {
        setForbidden(false);
        // The inbox and the menu badge count the same approvals: refresh both together.
        dispatch(getEventUserBadges());
        getApprovals({ scope, status: scope === 'mine' ? undefined : status || undefined })
            .then((r: any) => setItems(r.items || []))
            .catch(() => {
                setForbidden(scope === 'all');
                setItems([]);
            });
    }, [scope, status, dispatch]);

    useEffect(() => load(), [load]);

    useEffect(() => {
        const uuid = new URLSearchParams(location.search).get('approval');
        if (uuid) {
            getApproval(uuid).then((r: any) => setOpen(r.item)).catch(() => undefined);
        }
    }, [location.search]);

    const what = (a: Approval) => a.subjectType === 'peerActivity'
        ? (a.posts || []).map((p) => p.channel.name).join(', ')
        : (a.payload?.scenario?.name ? t('workflow:scenario') + ': ' + a.payload.scenario.name : t('workflow:approvalWorkflow'));

    const columns = [
        actionsColumn((a: Approval) => (
            <RowActions actions={[
                { icon: 'ri-checkbox-circle-fill', label: t('workflow:decide'), onClick: () => setOpen(a), hidden: !a.actions?.canDecide },
                { icon: 'ri-eye-fill', label: t('workflow:runDetails'), onClick: () => setOpen(a), hidden: !!a.actions?.canDecide },
            ]} />
        )),
        {
            name: t('workflow:whatToApprove'),
            grow: 4,
            cell: (a: Approval) => (
                <span className="mw-0">
                    <span className="d-block fw-medium text-truncate">{a.title}</span>
                    <span className="d-block text-muted fs-12 text-truncate">
                        <i className={a.subjectType === 'peerActivity' ? 'ri-article-line me-1' : 'ri-flow-chart me-1'} />{what(a)}
                    </span>
                </span>
            ),
        },
        { name: t('workflow:status'), cell: (a: Approval) => <ApprovalStatusBadge status={a.status} /> },
        { name: t('workflow:requestedBy'), grow: 2, cell: (a: Approval) => userLabel(a.requestedBy) },
        { name: t('workflow:requested'), grow: 2, cell: (a: Approval) => formatWorkflowDate(a.createdAt) },
        { name: t('workflow:decideBefore'), grow: 2, cell: (a: Approval) => a.status === 'pending' && a.timeoutAt ? formatWorkflowDate(a.timeoutAt) : '—' },
    ];

    const STATUSES = ['pending', 'approved', 'rejected', 'expired', 'cancelled'];

    const chip = (on: boolean) => 'btn btn-sm ' + (on ? 'btn-primary' : 'btn-light');

    return (
        <>
            <BreadCrumb title={t('menu:automationApprovals')} pageTitle={t('menu:automation')} />
            <ListCard
                title={t('workflow:approvalScope_' + scope)}
                headerExtra={<button type="button" className="btn btn-sm btn-soft-secondary" onClick={load}><i className="ri-refresh-line align-bottom" /></button>}
                filters={<>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {(['mine', 'requested', 'all'] as Scope[]).map((s) => (
                            <button key={s} type="button" className={chip(scope === s)} onClick={() => setScope(s)}>{t('workflow:approvalScope_' + s)}</button>
                        ))}
                        {scope !== 'mine' &&
                            <select className="form-select form-select-sm ms-2" style={{ width: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">{t('workflow:allStatuses')}</option>
                                {STATUSES.map((s) => <option key={s} value={s}>{t('workflow:approvalStatus_' + s)}</option>)}
                            </select>
                        }
                    </div>
                    <p className="text-muted mb-0 mt-3">{t('workflow:approvalsIntro')}</p>
                </>}
            >
                {items === null ? <Loader /> :
                    forbidden ? <div className="text-center text-muted py-5 fs-14">{t('workflow:approvalsAllForbidden')}</div> :
                        items.length === 0
                            ? <div className="text-center text-muted py-5"><i className="ri-checkbox-circle-line display-5 d-block mb-2" /><p className="fs-14 mb-0">{scope === 'mine' ? t('workflow:noApprovalsMine') : t('workflow:noApprovals')}</p></div>
                            : <WorkflowTable columns={columns as any} data={items} onRowClick={setOpen} />
                }
            </ListCard>
            <ApprovalModal approval={open} userLabel={userLabel} onClose={() => setOpen(null)} onDecided={() => { setOpen(null); load(); }} />
        </>
    );
};

export default ApprovalList;
